import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { LuLoaderCircle } from 'react-icons/lu';
import * as Sentry from '@sentry/react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { useResetQuestionnaire } from '../hooks/useResetQuestionnaire';

export const QuestionnaireSkeletonLoader = ({
  timeoutMs = 10000,
  hidePageTitle = false,
  resetKey,
}: {
  timeoutMs?: number;
  hidePageTitle?: boolean;
  // Progress marker (the questionnaire page): a change restarts the timeout
  resetKey?: string;
}) => {
  const [showLoader, setShowLoader] = useState(false);
  const [textIndex, setTextIndex] = useState(0);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const reset = useResetQuestionnaire();

  const loadingTexts = ['Analyse en cours', 'Réflexion', 'Finalisation'];

  // Switch to loader after 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(true);
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  // Terminal state: fires only when a single step stalls past timeoutMs. The
  // loader stays mounted while an empty page is skipped, so the save, the skip
  // and the next page's load would otherwise share one budget (ALDO-2882):
  // a page change counts as progress and restarts the timer.
  useEffect(() => {
    setHasTimedOut(false);
    const timer = setTimeout(() => {
      setHasTimedOut(true);
      Sentry.captureMessage(
        'QuestionnaireSkeletonLoader timed out waiting for a question',
        'warning',
      );
    }, timeoutMs);

    return () => clearTimeout(timer);
  }, [timeoutMs, resetKey]);

  // Cycle through loading texts
  useEffect(() => {
    if (showLoader && !hasTimedOut) {
      const interval = setInterval(() => {
        setTextIndex((prev) =>
          prev < loadingTexts.length - 1 ? prev + 1 : prev,
        );
      }, 1500);

      return () => clearInterval(interval);
    }
  }, [showLoader, hasTimedOut]);

  return (
    <div
      className="content-stretch flex flex-col items-start px-6 gap-5 w-full"
      data-name="Frame1618874221"
    >
      <div className="relative shrink-0 pt-1 w-full">
        <div className="bg-clip-padding border-0 border-[transparent] border-solid box-border content-stretch flex flex-col gap-3.5 items-start relative w-full">
          {/* Small skeleton bar / Loader */}
          {!showLoader ? (
            !hidePageTitle && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0, ease: [0.4, 0, 0.2, 1] }}
                className="h-4 rounded-lg shrink-0 w-24 bg-gradient-to-r from-[#eff6ff] to-[rgba(219,234,254,0.5)] relative overflow-hidden"
                data-name="Container"
              >
                <div
                  aria-hidden="true"
                  className="absolute border border-[rgba(219,234,254,0.3)] border-solid inset-0 pointer-events-none rounded-lg"
                />
                <div className="absolute inset-0 shimmer" />
              </motion.div>
            )
          ) : (
            <div className="flex items-center gap-3">
              <LuLoaderCircle className="h-3.5 w-3.5 animate-spin text-[#5f6368]" />
              <AnimatePresence mode="wait">
                <motion.p
                  key={textIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="font-['Inter:Medium',_sans-serif] font-medium leading-4 not-italic text-[#5f6368] text-sm tracking-widest whitespace-pre"
                >
                  {loadingTexts[textIndex]}...
                </motion.p>
              </AnimatePresence>
            </div>
          )}

          {/* Large skeleton box */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
            className="h-[90px] rounded-2xl shrink-0 w-full bg-gradient-to-r from-[#eff6ff] to-[rgba(219,234,254,0.5)] relative overflow-hidden"
            data-name="Container"
          >
            <div
              aria-hidden="true"
              className="absolute border border-[rgba(219,234,254,0.3)] border-solid inset-0 pointer-events-none rounded-2xl"
            />
            <div className="absolute inset-0 shimmer" />
          </motion.div>
        </div>
      </div>

      {/* Answer buttons skeletons */}
      <div
        className="basis-0 grow min-h-px min-w-px relative shrink-0 w-full"
        data-name="Container"
      >
        <div className="bg-clip-padding border-0 border-[transparent] border-solid box-border h-full relative w-full">
          {[0, 60, 120, 180].map((top, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.4,
                delay: 0.2 + index * 0.15,
                ease: [0.4, 0, 0.2, 1],
              }}
              className="absolute bg-gradient-to-r from-[#eff6ff] h-12 left-0 rounded-xl to-[rgba(219,234,254,0.5)] w-full overflow-hidden"
              style={{ top: `${top}px` }}
              data-name="Container"
            >
              <div
                aria-hidden="true"
                className="absolute border border-[rgba(219,234,254,0.3)] border-solid inset-0 pointer-events-none rounded-xl"
              />
              <div className="absolute inset-0 shimmer" />
            </motion.div>
          ))}
        </div>
      </div>

      <Dialog open={hasTimedOut}>
        <DialogContent className="nsm:w-[752px]" initialFocus={false}>
          <div className="px-5 nsm:px-8 py-4">
            <div className="flex flex-col justify-center gap-3">
              <DialogTitle className="text-xl font-medium">
                Le chargement prend plus de temps que prévu
              </DialogTitle>
              <div className="flex flex-col">
                <p className="font-light">
                  Vous pouvez réessayer en gardant vos réponses, ou recommencer
                  un nouveau questionnaire (cela effacera vos réponses).
                </p>
              </div>
              <div className="flex flex-col nsm:flex-row justify-center items-center gap-3 mt-4">
                <Button
                  variant="ctaSecondary"
                  size="lg"
                  onClick={() => reset({ clearStorage: true })}
                >
                  Recommencer un questionnaire
                </Button>
                <Button
                  variant="ctaPrimary"
                  size="lg"
                  onClick={() => window.location.reload()}
                >
                  Réessayer
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
